import classNames from 'classnames'
import React, { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { SeoPageRouter } from '@src/components/seo/seo-page-router'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { RichText } from '@src/quince-components/src'
import { Skeleton } from '@src/quince-components/src/atoms/skeleton'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getSeoMetapageData } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

import styles from '../../collectionPage.module.scss'

import type { CollectionTitleProps } from './collection-title.types'

export const CollectionTitleLegacy: React.FC<CollectionTitleProps> = props => {
  const {
    title,
    hideCollectionPageTitle,
    isRootCollectionPage,
    isJumpLink,
    subCollectionTitle,
    seoData,
    showLoader,
    alternateUrls,
    offerMessage,
  } = props

  const isI18nPreviewEnabled = useI18nPreviewContext()
  const { deviceType } = useDeviceInfo()
  const { regionDetails, locale } = useLocaleContext()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  // This is a alternative solution to update the seo data for the sub collection page, to support old collection page product type urls
  const updatedSeoData = useMemo(() => {
    const isAppendQuinceLocale = getUserAgnosticGlobalFeatureGatesFromMemory()['seo-title-append-quince-locale']
    if (subCollectionTitle && seoData) {
      return getSeoMetapageData(
        isI18nPreviewEnabled,
        {
          ...seoData,
          title: subCollectionTitle,
          // TODO: Sujeet - Fix the canonical url for sub collection pages
          canonicalUrl: '', // window.location.href,
        },
        locale,
        regionDetails,
        isAppendQuinceLocale,
        false,
      )
    }
    return getSeoMetapageData(isI18nPreviewEnabled, seoData, locale, regionDetails, isAppendQuinceLocale, false)
  }, [seoData, subCollectionTitle, isI18nPreviewEnabled, locale, regionDetails])

  const renderDesktopCollectionTitle = () => {
    if (hideCollectionPageTitle || !title) return null

    const titleClassName = `desktopOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${
      styles.collection_header
    } ${styles.newCLPFiltersTitle}`

    if (showLoader) {
      return (
        <div className={classNames(styles.collectionTitleLoaderContainer, styles.collectionTitleLoaderContainerBlock)}>
          <Skeleton className={styles.collectionTitleLoader} />
        </div>
      )
    }

    return (
      <h1 data-testid={TEST_LOCATORS.COLLECTION_TITLE} className={titleClassName}>
        {title}
      </h1>
    )
  }

  const renderMobileCollectionTitle = () => {
    if (hideCollectionPageTitle || isDesktop) return null

    const titleClassName = `mobileOnly d-flex justify-content--center font-family--grosa font-weight--500 text-transform--uppercase ${
      styles.collection_header
    } ${styles.newCLPFiltersMobileTitle} ${isRootCollectionPage ? styles.mobileNonCollectionPageTitle : ''}`

    if (showLoader) {
      return <Skeleton className={styles.collectionTitleLoader} />
    }

    return (
      <h1 data-testid={TEST_LOCATORS.COLLECTION_TITLE} className={titleClassName}>
        {isJumpLink ? subCollectionTitle : title}
      </h1>
    )
  }

  const shouldShowOfferMessage = !!offerMessage
  return (
    <div className={styles.collectionTitleWrapper}>
      {subCollectionTitle && (
        <SeoPageRouter
          seoMetaData={updatedSeoData}
          alternateUrls={alternateUrls}
          regionDetails={regionDetails}
          omitCanonical
        />
      )}
      {isDesktop ? renderDesktopCollectionTitle() : renderMobileCollectionTitle()}
      {shouldShowOfferMessage ? (
        <div className={styles.offerMessageContainer}>
          <RichText content={offerMessage} />
        </div>
      ) : null}
    </div>
  )
}
